// src/pages/ActivityDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/ActivityDetailPage.css';

interface ImageData {
  url: string;
  id: string;
}

interface ContentBlock {
  type: 'paragraph' | 'images';
  content: string;
  order: number;
}

interface Activity {
  _id: string;
  title: string;
  date: string;
  location: string;
  content: ContentBlock[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

const ActivityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://empower-future21-nodejs.vercel.app/api/activities/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch activity');
      }
      
      const data = await response.json();
      setActivity(data);
    } catch (err) {
      setError('Failed to load activity. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="activity-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading activity...</p>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="activity-detail-error">
        <h2>Error</h2>
        <p>{error || 'Activity not found'}</p>
        <Link to="/activities" className="back-link">Back to Activities</Link>
      </div>
    );
  }

  // Check if activity is published
  if (activity.status !== 'published') {
    return (
      <div className="activity-detail-error">
        <h2>Activity Not Available</h2>
        <p>This activity is not currently published.</p>
        <Link to="/activities" className="back-link">Back to Activities</Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={activity.title}
        description={activity.content.find(block => block.type === 'paragraph')?.content.substring(0, 160) || ''}
        keywords="activity, event, community development, education, empowerment"
      />
      
      <div className="activity-detail">
        <div className="container">
          <div className="activity-detail-meta">
            <span className="activity-detail-category">Activity</span>
            <span className="activity-detail-date">{formatDate(activity.date)}</span>
          </div>
          
          <h1 className="activity-detail-title">{activity.title}</h1>
          
          <div className="activity-detail-location">
            <span className="location-icon">📍</span> {activity.location}
          </div>
          
          <div className="activity-detail-content">
            {activity.content.map((block, index) => (
              <div key={index} className="content-block">
                {block.type === 'paragraph' && (
                  <p className="paragraph-block">{block.content}</p>
                )}
                
                {block.type === 'images' && (
                  <div className="images-block">
                    {JSON.parse(block.content || '[]').length === 1 ? (
                      // Single image display
                      <div className="single-image">
                        {JSON.parse(block.content || '[]').map((image: ImageData, imageIndex: number) => (
                          <img 
                            key={`img-${index}-${imageIndex}`}
                            src={image.url} 
                            alt={`${activity.title} - ${imageIndex + 1}`}
                          />
                        ))}
                      </div>
                    ) : (
                      // Multiple images grid
                      <div className="image-grid">
                        {JSON.parse(block.content || '[]').map((image: ImageData, imageIndex: number) => (
                          <img 
                            key={`img-${index}-${imageIndex}`}
                            src={image.url} 
                            alt={`${activity.title} - ${imageIndex + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="activity-detail-footer">
            <Link to="/activities" className="back-to-activities">
              ← Back to Activities
            </Link>
            
            <div className="activity-share">
              <h3>Share this activity</h3>
              <div className="share-buttons">
                <button className="share-btn facebook">Facebook</button>
                <button className="share-btn twitter">Twitter</button>
                <button className="share-btn linkedin">LinkedIn</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivityDetailPage;